/**
 * AssetsLibrary.ts;
 * AMÉLIORATION: Gestion d'une bibliothèque d'assets avec IndexedDB (fallback localStorage).
 * Supporte jusqu'à ~50MB+ de stockage au lieu de ~5-10MB avec localStorage.
 */
import { logger } from './utils/ProfessionalLogger';

export interface AssetItem {
  id: string;
  name: string;
  type: 'image' | 'template';
  dataURL: string;
  thumbnail: string;
  tags: string[];
  createdAt: Date;
  lastUsed?: Date;
  size: { width: number; height: number };
}

interface IDBAssetItem extends Omit<AssetItem, 'createdAt' | 'lastUsed'> {
  createdAt: string;
  lastUsed?: string;
}

export class AssetsLibrary {
  private static _instance: AssetsLibrary | null = null;

  /** Accès singleton — partage le cache mémoire/IndexedDB entre composants. */
  static getInstance(): AssetsLibrary {
    if (!AssetsLibrary._instance) {
      AssetsLibrary._instance = new AssetsLibrary();
    }
    return AssetsLibrary._instance;
  }

  private assets = new Map<string, AssetItem>();
  private readonly storageKey = 'snapbooth_assets_library';
  private readonly maxAssets = 100;
  private readonly dbName = 'snapbooth_assets_db';
  private readonly dbVersion = 1;
  private readonly storeName = 'assets';
  private db: IDBDatabase | null = null;
  private useIndexedDB: boolean = false;

  constructor() {
    this.initializeStorage();
  }

  /**
   * AMÉLIORATION: Initialise le stockage (IndexedDB avec fallback localStorage)
   */
  private async initializeStorage(): Promise<void> {
    try {
      // Vérifier si IndexedDB est disponible
      if (typeof indexedDB !== 'undefined') {
        this.db = await this.openDatabase();
        if (this.db) {
          this.useIndexedDB = true;
          logger.info('✅ IndexedDB initialisé pour AssetsLibrary');
          await this.loadFromIndexedDB();
          // Migrer les données depuis localStorage si nécessaire
          await this.migrateFromLocalStorage();
          return;
        }
      }
    } catch (error) {
      logger.warn('IndexedDB non disponible, utilisation de localStorage', error);
    }

    // Fallback vers localStorage
    this.useIndexedDB = false;
    this.loadFromStorage();
  }

  /**
   * AMÉLIORATION: Ouvre la base de données IndexedDB
   */
  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('lastUsed', 'lastUsed', { unique: false });
        }
      };
    });
  }

  /**
   * AMÉLIORATION: Migre les données depuis localStorage vers IndexedDB
   */
  private async migrateFromLocalStorage(): Promise<void> {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return;

      const parsed = JSON.parse(stored) as Array<
        [
          string,
          Omit<AssetItem, 'createdAt' | 'lastUsed'> & { createdAt: string; lastUsed?: string },
        ]
      >;

      if (parsed.length === 0) return;

      logger.info(`🔄 Migration de ${parsed.length} assets depuis localStorage vers IndexedDB`);

      for (const [id, data] of parsed) {
        const asset: IDBAssetItem = {
          ...data,
          id,
          createdAt: data.createdAt,
          lastUsed: data.lastUsed,
        };
        await this.saveToIndexedDB(asset);
      }

      // Supprimer les données de localStorage après migration
      localStorage.removeItem(this.storageKey);
      logger.info('✅ Migration terminée, localStorage nettoyé');
    } catch (error) {
      logger.error('Erreur lors de la migration depuis localStorage', error);
    }
  }

  /**
   * AMÉLIORATION: Charge les assets depuis IndexedDB
   */
  private async loadFromIndexedDB(): Promise<void> {
    if (!this.db) return;

    try {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      await new Promise<void>((resolve, reject) => {
        request.onsuccess = () => {
          this.assets.clear();
          request.result.forEach((item: IDBAssetItem) => {
            this.assets.set(item.id, {
              ...item,
              createdAt: new Date(item.createdAt),
              lastUsed: item.lastUsed ? new Date(item.lastUsed) : undefined,
            });
          });
          logger.info(`${this.assets.size} assets chargés depuis IndexedDB`);
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logger.error('Erreur lors du chargement depuis IndexedDB', error);
      this.assets.clear();
    }
  }

  /**
   * AMÉLIORATION: Sauvegarde un asset dans IndexedDB
   */
  private async saveToIndexedDB(asset: IDBAssetItem): Promise<void> {
    if (!this.db) return;

    try {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      await new Promise<void>((resolve, reject) => {
        const request = store.put(asset);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde dans IndexedDB', error);
      throw error;
    }
  }
  /**
   * Ajoute un asset à la bibliothèque et renvoie son identifiant.
   */
  public async addAsset(
    name: string,
    dataURL: string,
    type: 'image' | 'template',
    tags: string[] = []
  ): Promise<string> {
    const id = this.generateId();
    const [thumbnail, size] = await Promise.all([
      this.generateThumbnail(dataURL),
      this.getImageSize(dataURL),
    ]);
    const asset: AssetItem = {
      id,
      name,
      type,
      dataURL,
      thumbnail,
      tags,
      createdAt: new Date(),
      size,
    };
    this.assets.set(id, asset);

    // AMÉLIORATION: Sauvegarder dans IndexedDB ou localStorage selon la disponibilité
    if (this.useIndexedDB) {
      const idbAsset: IDBAssetItem = {
        ...asset,
        createdAt: asset.createdAt.toISOString(),
        lastUsed: asset.lastUsed?.toISOString(),
      };
      await this.saveToIndexedDB(idbAsset);
    } else {
      this.saveToStorage();
    }

    logger.info(`Asset ajouté: ${name} (${type})`);
    return id;
  }
  public async removeAsset(id: string): Promise<boolean> {
    const removed = this.assets.delete(id);
    if (removed) {
      // AMÉLIORATION: Supprimer depuis IndexedDB ou localStorage
      if (this.useIndexedDB && this.db) {
        try {
          const transaction = this.db.transaction([this.storeName], 'readwrite');
          const store = transaction.objectStore(this.storeName);
          await new Promise<void>((resolve, reject) => {
            const request = store.delete(id);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
          });
        } catch (error) {
          logger.error('Erreur lors de la suppression dans IndexedDB', error);
        }
      } else {
        this.saveToStorage();
      }
      logger.info(`Asset supprimé: ${id}`);
    }
    return removed;
  }
  public getAsset(id: string): AssetItem | null {
    return this.assets.get(id) || null;
  }
  public getAllAssets(): AssetItem[] {
    return Array.from(this.assets.values());
  }
  public getAssetsByType(type: 'image' | 'template'): AssetItem[] {
    return this.getAllAssets().filter(asset => asset.type === type);
  }
  public searchAssets(query: string): AssetItem[] {
    const needle = query.toLowerCase().trim();
    if (!needle) {
      return this.getAllAssets();
    }
    return this.getAllAssets().filter(
      asset =>
        asset.name.toLowerCase().includes(needle) ||
        asset.tags.some(tag => tag.toLowerCase().includes(needle))
    );
  }
  public filterAssetsByTags(tags: string[]): AssetItem[] {
    if (tags.length === 0) {
      return this.getAllAssets();
    }
    const set = new Set(tags);
    return this.getAllAssets().filter(asset => asset.tags.every(tag => set.has(tag)));
  }
  public async updateAssetTags(id: string, tags: string[]): Promise<boolean> {
    const asset = this.assets.get(id);
    if (!asset) {
      return false;
    }
    asset.tags = [...tags];

    // AMÉLIORATION: Sauvegarder dans IndexedDB ou localStorage
    if (this.useIndexedDB) {
      const idbAsset: IDBAssetItem = {
        ...asset,
        createdAt: asset.createdAt.toISOString(),
        lastUsed: asset.lastUsed?.toISOString(),
      };
      await this.saveToIndexedDB(idbAsset);
    } else {
      this.saveToStorage();
    }
    return true;
  }

  public async updateAssetName(id: string, name: string): Promise<boolean> {
    const asset = this.assets.get(id);
    if (!asset) {
      return false;
    }
    asset.name = name;

    // AMÉLIORATION: Sauvegarder dans IndexedDB ou localStorage
    if (this.useIndexedDB) {
      const idbAsset: IDBAssetItem = {
        ...asset,
        createdAt: asset.createdAt.toISOString(),
        lastUsed: asset.lastUsed?.toISOString(),
      };
      await this.saveToIndexedDB(idbAsset);
    } else {
      this.saveToStorage();
    }
    return true;
  }

  public async markAssetAsUsed(id: string): Promise<void> {
    const asset = this.assets.get(id);
    if (asset) {
      asset.lastUsed = new Date();

      // AMÉLIORATION: Sauvegarder dans IndexedDB ou localStorage
      if (this.useIndexedDB) {
        const idbAsset: IDBAssetItem = {
          ...asset,
          createdAt: asset.createdAt.toISOString(),
          lastUsed: asset.lastUsed.toISOString(),
        };
        await this.saveToIndexedDB(idbAsset);
      } else {
        this.saveToStorage();
      }
    }
  }
  public getRecentlyUsedAssets(limit: number = 10): AssetItem[] {
    return this.getAllAssets()
      .filter((asset): asset is AssetItem & { lastUsed: Date } => asset.lastUsed !== undefined)
      .sort((a, b) => b.lastUsed.getTime() - a.lastUsed.getTime())
      .slice(0, limit);
  }
  public getAllTags(): string[] {
    const tags = new Set<string>();
    this.getAllAssets().forEach(asset => {
      asset.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort((a, b) => a.localeCompare(b));
  }
  public async cleanupOldAssets(days: number = 30): Promise<number> {
    const limit = new Date();
    limit.setDate(limit.getDate() - days);
    let removed = 0;
    const idsToRemove: string[] = [];

    for (const [id, asset] of this.assets.entries()) {
      if (!asset.lastUsed || asset.lastUsed < limit) {
        this.assets.delete(id);
        idsToRemove.push(id);
        removed++;
      }
    }

    if (removed > 0) {
      // AMÉLIORATION: Supprimer depuis IndexedDB ou localStorage
      if (this.useIndexedDB && this.db) {
        try {
          const transaction = this.db.transaction([this.storeName], 'readwrite');
          const store = transaction.objectStore(this.storeName);
          await Promise.all(
            idsToRemove.map(
              id =>
                new Promise<void>((resolve, reject) => {
                  const request = store.delete(id);
                  request.onsuccess = () => resolve();
                  request.onerror = () => reject(request.error);
                })
            )
          );
        } catch (error) {
          logger.error('Erreur lors du nettoyage dans IndexedDB', error);
        }
      } else {
        this.saveToStorage();
      }
      logger.info(`${removed} anciens assets supprimés`);
    }
    return removed;
  }

  public async enforceMaxAssets(): Promise<number> {
    if (this.assets.size <= this.maxAssets) {
      return 0;
    }
    const sorted = Array.from(this.assets.values()).sort(
      (a, b) => a.createdAt.getTime() - b.createdAt.getTime()
    );
    let removed = 0;
    const idsToRemove: string[] = [];

    while (this.assets.size > this.maxAssets && sorted.length > 0) {
      const asset = sorted.shift();
      if (asset) {
        this.assets.delete(asset.id);
        idsToRemove.push(asset.id);
        removed++;
      }
    }

    if (removed > 0) {
      // AMÉLIORATION: Supprimer depuis IndexedDB ou localStorage
      if (this.useIndexedDB && this.db) {
        try {
          const transaction = this.db.transaction([this.storeName], 'readwrite');
          const store = transaction.objectStore(this.storeName);
          await Promise.all(
            idsToRemove.map(
              id =>
                new Promise<void>((resolve, reject) => {
                  const request = store.delete(id);
                  request.onsuccess = () => resolve();
                  request.onerror = () => reject(request.error);
                })
            )
          );
        } catch (error) {
          logger.error("Erreur lors de l'enforcement dans IndexedDB", error);
        }
      } else {
        this.saveToStorage();
      }
      logger.info(`${removed} assets supprimés (limite atteinte)`);
    }
    return removed;
  }

  public async clearAll(): Promise<void> {
    this.assets.clear();

    // AMÉLIORATION: Vider IndexedDB ou localStorage
    if (this.useIndexedDB && this.db) {
      try {
        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        await new Promise<void>((resolve, reject) => {
          const request = store.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      } catch (error) {
        logger.error("Erreur lors du vidage d'IndexedDB", error);
      }
    } else {
      this.saveToStorage();
    }

    logger.info("Bibliothèque d'assets vidée");
  }
  public getStats(): {
    totalAssets: number;
    imagesCount: number;
    templatesCount: number;
    totalSize: number;
    oldestAsset?: Date;
    newestAsset?: Date;
  } {
    const all = this.getAllAssets();
    const images = all.filter(asset => asset.type === 'image');
    const templates = all.filter(asset => asset.type === 'template');
    const totalSize = all.reduce((sum, asset) => sum + asset.size.width * asset.size.height, 0);
    const sortedDates = all.map(asset => asset.createdAt.getTime()).sort((a, b) => a - b);
    return {
      totalAssets: all.length,
      imagesCount: images.length,
      templatesCount: templates.length,
      totalSize,
      oldestAsset: sortedDates.length ? new Date(sortedDates[0]) : undefined,
      newestAsset: sortedDates.length ? new Date(sortedDates[sortedDates.length - 1]) : undefined,
    };
  }
  private generateId(): string {
    return `asset_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }
  private async generateThumbnail(dataURL: string): Promise<string> {
    return new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Fix CORS pour l'export
      img.onload = () => {
        const size = 100;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(dataURL);
          return;
        }
        const ratio = Math.min(size / img.width, size / img.height);
        const width = img.width * ratio;
        const height = img.height * ratio;
        const offsetX = (size - width) / 2;
        const offsetY = (size - height) / 2;
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, offsetX, offsetY, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = () => {
        logger.error("Erreur lors du chargement de l'image pour la miniature");
        resolve(dataURL);
      };
      img.src = dataURL;
    });
  }
  private async getImageSize(dataURL: string): Promise<{ width: number; height: number }> {
    return new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Fix CORS pour l'export
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        logger.error("Erreur lors du chargement de l'image pour obtenir les dimensions");
        resolve({ width: 0, height: 0 });
      };
      img.src = dataURL;
    });
  }
  private saveToStorage(): void {
    try {
      const serialisable = Array.from(this.assets.entries()).map(([id, asset]) => [
        id,
        {
          ...asset,
          createdAt: asset.createdAt.toISOString(),
          lastUsed: asset.lastUsed ? asset.lastUsed.toISOString() : undefined,
        },
      ]);
      localStorage.setItem(this.storageKey, JSON.stringify(serialisable));
    } catch (error) {
      logger.error("Erreur lors de la sauvegarde de la bibliothèque d'assets", error);
    }
  }
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) {
        return;
      }
      const parsed = JSON.parse(stored) as Array<
        [
          string,
          Omit<AssetItem, 'createdAt' | 'lastUsed'> & { createdAt: string; lastUsed?: string },
        ]
      >;
      this.assets.clear();
      parsed.forEach(([id, data]) => {
        this.assets.set(id, {
          ...data,
          createdAt: new Date(data.createdAt),
          lastUsed: data.lastUsed ? new Date(data.lastUsed) : undefined,
        });
      });
      logger.info(`${this.assets.size} assets chargés depuis le stockage`);
    } catch (error) {
      logger.error("Erreur lors du chargement de la bibliothèque d'assets", error);
      this.assets.clear();
    }
  }
}
